import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageCircle, X, Send, Sparkles, Trash2 } from 'lucide-react';
import { useAuth } from '../lib/auth';

const API = process.env.REACT_APP_BACKEND_URL;
const SID_KEY = 'ssl_assistant_sid';
const getSid = () => { let s = localStorage.getItem(SID_KEY); if (!s) { s = `as_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`; localStorage.setItem(SID_KEY, s); } return s; };
const SUGGEST = {
  fr: ['Comment créer ma boutique ?', 'Comment payer avec Wave ?', 'Où est ma commande ?', 'Comment lancer un live ?'],
  wo: ['Naka laa mëna ubbi sama bitik ?', 'Naka laa fey ak Wave ?', 'Fan la sama commande nekk ?', 'Naka laa tàmbali live ?'],
  auto: ['Comment créer ma boutique ?', 'Naka laa fey ak Wave ?', 'Où est ma commande ?', 'Naka laa tàmbali live ?'],
};

const renderText = (text, nav) => text.split(/(\[\s*\/[a-zA-Z0-9_\-/]+\s*\])/g).map((part, i) => {
  const m = part.match(/^\[\s*(\/[a-zA-Z0-9_\-/]+)\s*\]$/);
  return m ? <button key={i} onClick={() => nav(m[1])} className="underline decoration-[#E8B84A]/60 text-[#F5D98B] hover:text-white">{m[1]}</button> : <span key={i}>{part}</span>;
});

export function AssistantWidget() {
  const { user } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState(() => localStorage.getItem('ssl_assistant_lang') || 'auto');
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const bottom = useRef(null);
  const sid = getSid();

  useEffect(() => { if (open && msgs.length === 0) fetch(`${API}/api/assistant/history?session_id=${sid}`).then((r) => r.json()).then((h) => Array.isArray(h) && h.length && setMsgs((cur) => (cur.length ? cur : h.map((m) => ({ role: m.role, content: m.content }))))).catch(() => {}); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { bottom.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, open]);
  useEffect(() => { localStorage.setItem('ssl_assistant_lang', lang); }, [lang]);
  if (loc.pathname.startsWith('/live/')) return null;

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput(''); setBusy(true);
    setMsgs((m) => [...m, { role: 'user', content: message }, { role: 'assistant', content: '' }]);
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('ssl_token');
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`${API}/api/assistant/chat`, { method: 'POST', headers, body: JSON.stringify({ session_id: sid, message, lang, page: loc.pathname }) });
      if (!res.ok || !res.body) throw new Error((await res.json().catch(() => ({})))?.detail || 'Erreur');
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = '';
      for (;;) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split('\n\n'); buf = lines.pop();
        for (const l of lines) {
          if (!l.startsWith('data: ')) continue;
          const d = JSON.parse(l.slice(6));
          if (d.t) setMsgs((m) => { const c = [...m]; const last = c[c.length - 1]; if (!last || last.role !== 'assistant') c.push({ role: 'assistant', content: d.t }); else c[c.length - 1] = { role: 'assistant', content: last.content + d.t }; return c; });
        }
      }
    } catch (e) {
      setMsgs((m) => { const c = [...m]; const last = c[c.length - 1]; c[last && last.role === 'assistant' ? c.length - 1 : c.length] = { role: 'assistant', content: `Désolé — ${e.message}. Réessaie dans un instant.` }; return c; });
    } finally { setBusy(false); }
  };
  const reset = () => { localStorage.removeItem(SID_KEY); setMsgs([]); };

  return (
    <>
      <button onClick={() => setOpen((o) => !o)} data-testid="assistant-toggle" aria-label="Assistant"
        className={`fixed bottom-5 right-5 z-[60] h-14 w-14 rounded-full btn-gold grid place-items-center shadow-2xl transition-transform hover:scale-105 ${open ? 'rotate-90' : ''}`}>
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
        {!open && <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-[#0b0f17] border border-[#E8B84A] grid place-items-center"><Sparkles className="h-2.5 w-2.5 text-[#F5D98B]" /></span>}
      </button>
      {open && (
        <div data-testid="assistant-panel" className="fixed bottom-24 right-4 left-4 sm:left-auto sm:w-[400px] z-[60] glass-gold rounded-3xl flex flex-col max-h-[70vh] overflow-hidden reveal">
          <div className="p-4 flex items-center justify-between gap-2 border-b border-white/5">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 rounded-2xl btn-gold grid place-items-center shrink-0"><Sparkles className="h-5 w-5" /></div>
              <div className="min-w-0"><div className="font-display font-bold leading-tight">Jàppale — Assistant</div><div className="text-[11px] text-slate-400 truncate">Français · Wolof · répond 24h/24</div></div>
            </div>
            <div className="flex items-center gap-1">
              <div className="flex rounded-full bg-[#0b1120] p-0.5 border border-white/10" data-testid="assistant-lang">
                {[['auto', 'Auto'], ['fr', 'FR'], ['wo', 'Wolof']].map(([k, l]) => <button key={k} onClick={() => setLang(k)} data-testid={`assistant-lang-${k}`} className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition-colors ${lang === k ? 'btn-gold' : 'text-slate-400 hover:text-white'}`}>{l}</button>)}
              </div>
              <button onClick={reset} data-testid="assistant-reset" title="Nouvelle conversation" className="h-8 w-8 grid place-items-center rounded-full text-slate-500 hover:text-rose-300"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px]" data-testid="assistant-messages">
            {msgs.length === 0 && (
              <div className="space-y-3">
                <div className="glass rounded-2xl p-3 text-sm text-slate-200">{lang === 'wo' ? 'Salaam aleekum ! Man Jàppale laa, laaj ma lu la neex ci JOTALY.' : 'Salaam aleekum ! Je suis Jàppale. Pose-moi ta question en français ou en wolof — boutique, paiement Wave, commandes, lives…'}</div>
                <div className="flex flex-wrap gap-2">{SUGGEST[lang].map((s) => <button key={s} onClick={() => send(s)} data-testid="assistant-suggest" className="text-xs px-3 py-1.5 rounded-full btn-ghost-gold">{s}</button>)}</div>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div data-testid={`assistant-msg-${m.role}`} className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${m.role === 'user' ? 'btn-gold rounded-br-md' : 'glass text-slate-100 rounded-bl-md'}`}>
                  {m.content ? renderText(m.content, nav) : <span className="inline-flex gap-1"><span className="h-1.5 w-1.5 rounded-full bg-[#E8B84A] animate-bounce" /><span className="h-1.5 w-1.5 rounded-full bg-[#E8B84A] animate-bounce [animation-delay:120ms]" /><span className="h-1.5 w-1.5 rounded-full bg-[#E8B84A] animate-bounce [animation-delay:240ms]" /></span>}
                </div>
              </div>
            ))}
            <div ref={bottom} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-white/5 flex items-center gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="assistant-input" placeholder={lang === 'wo' ? 'Bindal sa laaj…' : 'Écris ta question (FR ou Wolof)…'} disabled={busy}
              className="flex-1 px-4 py-2.5 rounded-full bg-[#0b1120]/80 border border-white/10 text-white placeholder-slate-500 focus:border-[#E8B84A]/70 outline-none text-sm" />
            <button type="submit" disabled={busy || !input.trim()} data-testid="assistant-send" className="h-10 w-10 rounded-full btn-gold grid place-items-center disabled:opacity-50"><Send className="h-4 w-4" /></button>
          </form>
          {user && <div className="px-4 pb-2 text-[10px] text-slate-500">Connecté : {user.name} — je connais tes commandes.</div>}
        </div>
      )}
    </>
  );
}
